import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchProjects, deleteProject } from '@/lib/api';

export function ProjectsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const projects = data?.data ?? [];

  return (
    <div className="px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Moje projekty</h1>
        <Link
          to="/editor/new"
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          + Nowy
        </Link>
      </div>

      {isLoading && <p className="py-12 text-center text-sm text-gray-400">Ładuję projekty...</p>}

      {!isLoading && projects.length === 0 && (
        <div className="flex flex-col items-center py-12 text-center">
          <p className="mb-2 text-lg text-gray-500">Brak projektów</p>
          <p className="text-sm text-gray-400">Stwórz swój pierwszy wzór koralikowy!</p>
        </div>
      )}

      {projects.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {projects.map((project) => {
            const gridData = project.gridData as { technique?: string; width?: number; height?: number } | null;
            return (
              <div
                key={project.id}
                className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                {/* Thumbnail / placeholder */}
                <Link
                  to={`/editor/${project.id}`}
                  className="flex h-28 items-center justify-center bg-gray-50"
                >
                  {project.thumbnail ? (
                    <img
                      src={project.thumbnail}
                      alt={project.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl text-gray-300">
                      {gridData?.technique === 'peyote' ? '◇' : gridData?.technique === 'brick' ? '▬' : '▦'}
                    </span>
                  )}
                </Link>

                {/* Info */}
                <div className="px-3 py-2">
                  <Link to={`/editor/${project.id}`} className="block">
                    <h3 className="truncate text-sm font-medium text-gray-800">{project.name}</h3>
                    <p className="text-[10px] text-gray-400">
                      {gridData?.technique ?? 'loom'} · {gridData?.width ?? '?'}×{gridData?.height ?? '?'}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {new Date(project.updatedAt).toLocaleDateString('pl-PL')}
                    </p>
                  </Link>
                </div>

                {/* Delete */}
                <button
                  onClick={() => {
                    if (window.confirm('Usunąć ten projekt?')) {
                      deleteMutation.mutate(project.id);
                    }
                  }}
                  className="absolute right-1 top-1 hidden rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] text-red-500 group-hover:block"
                >
                  Usuń
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
